import React, { useRef } from 'react'
import type { TFunction } from 'i18next'
import { MetricCard } from '../../../../atoms/MetricCard'
import { TooltipMetricExplanation } from '../../../../TooltipMetricExplanation'
import { ChartHeader } from '../../../../molecules/ChartComponents'
import { downloadChartAsPng, sanitizeFilename } from '../../../../../utils'
import { type EmploymentAnalysis } from '../../employmentService'
import {
  EmploymentTrendChart,
  EmploymentTrendChartRef,
} from '../charts/EmploymentTrendChart'
import {
  SectorCompositionChart,
  SectorCompositionChartRef,
} from '../charts/SectorCompositionChart'
import {
  TabContentContainer,
  MetricsGrid,
  ChartGrid,
  ChartContainer,
} from '../../EmploymentTab.styles'

export interface OverviewTabProps {
  data: EmploymentAnalysis | null
  t: TFunction
}

export const OverviewTab: React.FC<OverviewTabProps> = ({ data, t }) => {
  const trendChartRef = useRef<EmploymentTrendChartRef>(null)
  const sectorChartRef = useRef<SectorCompositionChartRef>(null)

  if (!data) return null

  const { current, realEstateInsights } = data

  // Calculate 10-year CAGR
  const cagr10Year =
    (current.total_employment_evolution_10_year || 0) !== 0
      ? (Math.pow(1 + (current.total_employment_evolution_10_year || 0), 0.1) -
          1) *
        100
      : 0

  const getGrowthDisplayValue = (evolution: number): string => {
    const percentage = Math.abs(evolution * 100)
    const sign = evolution >= 0 ? '+' : '-'
    return `${sign}${percentage.toFixed(1)}%`
  }

  const getGrowthVariant = (
    evolution: number
  ): 'success' | 'warning' | 'danger' | 'default' => {
    if (evolution > 0.05) return 'success'
    if (evolution > 0) return 'default'
    if (evolution > -0.05) return 'warning'
    return 'danger'
  }

  const getDominantSectorName = () => {
    const totalEmp = current.total_employment || 0
    const sector1Pct =
      totalEmp > 0 ? ((current.employment_sector_1 || 0) / totalEmp) * 100 : 0
    const sector2Pct =
      totalEmp > 0 ? ((current.employment_sector_2 || 0) / totalEmp) * 100 : 0
    const sector3Pct =
      totalEmp > 0 ? ((current.employment_sector_3 || 0) / totalEmp) * 100 : 0

    if (sector3Pct > sector1Pct && sector3Pct > sector2Pct)
      return t('sectors.services')
    if (sector2Pct > sector1Pct) return t('sectors.industry')
    return t('sectors.agriculture')
  }

  const getDominantSectorPercentage = () => {
    const totalEmp = current.total_employment || 0
    const sector1Pct =
      totalEmp > 0 ? ((current.employment_sector_1 || 0) / totalEmp) * 100 : 0
    const sector2Pct =
      totalEmp > 0 ? ((current.employment_sector_2 || 0) / totalEmp) * 100 : 0
    const sector3Pct =
      totalEmp > 0 ? ((current.employment_sector_3 || 0) / totalEmp) * 100 : 0
    return Math.max(sector1Pct, sector2Pct, sector3Pct).toFixed(1)
  }

  return (
    <TabContentContainer>
      {/* Key Metrics */}
      <MetricsGrid>
        <MetricCard
          title={t('metrics.totalEmployment.title')}
          value={(current.total_employment || 0).toLocaleString()}
          subtitle={t('metrics.totalEmployment.subtitle')}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.totalEmployment.title')}
              shortDescription={t('tooltips.totalEmployment.shortDescription')}
              calculation={t('tooltips.totalEmployment.calculation')}
              importance={t('tooltips.totalEmployment.importance')}
              limitations={t('tooltips.totalEmployment.limitations')}
              source={t('tooltips.totalEmployment.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.employmentGrowth.title')}
          value={getGrowthDisplayValue(
            current.total_employment_evolution_10_year || 0
          )}
          subtitle={t('metrics.employmentGrowth.subtitle')}
          variant={getGrowthVariant(
            current.total_employment_evolution_10_year || 0
          )}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.employmentGrowth.title')}
              shortDescription={t('tooltips.employmentGrowth.shortDescription')}
              calculation={t('tooltips.employmentGrowth.calculation')}
              importance={t('tooltips.employmentGrowth.importance')}
              limitations={t('tooltips.employmentGrowth.limitations')}
              source={t('tooltips.employmentGrowth.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.cagr.title')}
          value={`${cagr10Year >= 0 ? '+' : ''}${cagr10Year.toFixed(2)}%`}
          subtitle={t('metrics.cagr.subtitle')}
          variant={getGrowthVariant(
            current.total_employment_evolution_10_year || 0
          )}
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.cagr.title')}
              shortDescription={t('tooltips.cagr.shortDescription')}
              calculation={t('tooltips.cagr.calculation')}
              importance={t('tooltips.cagr.importance')}
              limitations={t('tooltips.cagr.limitations')}
              source={t('tooltips.cagr.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.fullTimeRatio.title')}
          value={`${realEstateInsights.fullTimeRatio.toFixed(1)}%`}
          subtitle={t('metrics.fullTimeRatio.subtitle')}
          variant={
            realEstateInsights.fullTimeRatio > 80
              ? 'success'
              : realEstateInsights.fullTimeRatio > 70
                ? 'default'
                : 'warning'
          }
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.fullTimeRatio.title')}
              shortDescription={t('tooltips.fullTimeRatio.shortDescription')}
              calculation={t('tooltips.fullTimeRatio.calculation')}
              importance={t('tooltips.fullTimeRatio.importance')}
              limitations={t('tooltips.fullTimeRatio.limitations')}
              source={t('tooltips.fullTimeRatio.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.dominantSector.title')}
          value={getDominantSectorName()}
          subtitle={t('metrics.dominantSector.subtitle', {
            percentage: getDominantSectorPercentage(),
          })}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.dominantSector.title')}
              shortDescription={t('tooltips.dominantSector.shortDescription')}
              calculation={t('tooltips.dominantSector.calculation')}
              importance={t('tooltips.dominantSector.importance')}
              limitations={t('tooltips.dominantSector.limitations')}
              source={t('tooltips.dominantSector.source')}
            />
          }
        />

        <MetricCard
          title={t('metrics.jobsPerEstablishment.title')}
          value={realEstateInsights.jobsPerEstablishment.toFixed(1)}
          subtitle={t('metrics.jobsPerEstablishment.subtitle')}
          variant="default"
          tooltip={
            <TooltipMetricExplanation
              title={t('tooltips.jobsPerEstablishment.title')}
              shortDescription={t(
                'tooltips.jobsPerEstablishment.shortDescription'
              )}
              calculation={t('tooltips.jobsPerEstablishment.calculation')}
              importance={t('tooltips.jobsPerEstablishment.importance')}
              limitations={t('tooltips.jobsPerEstablishment.limitations')}
              source={t('tooltips.jobsPerEstablishment.source')}
            />
          }
        />
      </MetricsGrid>

      {/* Charts */}
      <ChartGrid>
        <ChartContainer>
          <ChartHeader
            title={t('charts.employmentTrend.title')}
            subtitle={t('charts.employmentTrend.subtitle')}
            onDownload={() => {
              const dataUrl = trendChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.employmentTrend.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <EmploymentTrendChart
            ref={trendChartRef}
            data={data.trends}
            height={300}
            t={t}
          />
        </ChartContainer>

        <ChartContainer>
          <ChartHeader
            title={t('charts.sectorComposition.title')}
            subtitle={t('charts.sectorComposition.subtitle')}
            onDownload={() => {
              const dataUrl = sectorChartRef.current?.exportChart()
              downloadChartAsPng(
                dataUrl,
                sanitizeFilename(t('charts.sectorComposition.title'))
              )
            }}
            downloadLabel={t('common.downloadChart')}
          />
          <SectorCompositionChart
            ref={sectorChartRef}
            sector1={current.employment_sector_1 || 0}
            sector2={current.employment_sector_2 || 0}
            sector3={current.employment_sector_3 || 0}
            height={300}
            t={t}
          />
        </ChartContainer>
      </ChartGrid>
    </TabContentContainer>
  )
}
